import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { homepageAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorBoundary from './ErrorBoundary';
import './ContentManager.css';

function ContentManager() {
  const navigate = useNavigate();
  
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filter and search state
  const [filters, setFilters] = useState({
    status: 'all',
    contentType: 'all',
    search: ''
  });
  
  // Selection state for bulk operations
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [, setShowBulkActions] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState({
    totalContent: 0,
    publishedContent: 0,
    draftContent: 0,
    archivedContent: 0,
    recentViews: 0,
    popularContent: []
  });

  const updateAnalytics = useCallback((content) => {
    const stats = content.reduce((acc, item) => {
      const next = { ...acc };
      next.totalContent++;
      const status = item.status || 'draft';
      if (status === 'published') next.publishedContent++;
      else if (status === 'draft') next.draftContent++;
      else if (status === 'archived') next.archivedContent++;
      return next;
    }, {
      totalContent: 0,
      publishedContent: 0,
      draftContent: 0,
      archivedContent: 0
    });

    setAnalytics(prev => ({ ...prev, ...stats }));
  }, []);

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await homepageAPI.listContent();
      
      if (response.success && Array.isArray(response.data)) {
        setContentList(response.data);
        updateAnalytics(response.data);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading content:', err);
    } finally {
      setLoading(false);
    }
  }, [updateAnalytics]);

  const loadAnalytics = useCallback(async () => {
    // Mock analytics data - in real implementation, this would come from API
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));

    setAnalytics(prev => ({
      ...prev,
      recentViews: Math.floor(Math.random() * 1000) + 100,
      popularContent: [
        { title: 'Flying Safety Guidelines', views: 245 },
        { title: 'Weather Conditions Update', views: 189 },
        { title: 'New Site: Bowbeat Hill', views: 156 }
      ]
    }));
  }, []);

  useEffect(() => {
    // On mount `loading` already starts true and `error` null, so run the
    // fetches directly here rather than through the shared loaders (whose
    // synchronous state resets would be a setState-in-effect). The shared
    // loaders remain for imperative refreshes (e.g. after bulk actions).
    let ignore = false;
    ;(async () => {
      try {
        const response = await homepageAPI.listContent();
        if (ignore) return;
        if (response.success && Array.isArray(response.data)) {
          setContentList(response.data);
          updateAnalytics(response.data);
        } else {
          throw new Error('Invalid response format from server');
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message);
          console.error('Error loading content:', err);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    ;(async () => {
      try {
        await loadAnalytics();
      } catch (err) {
        console.error('Error loading analytics:', err);
      }
    })();
    return () => { ignore = true; };
  }, [updateAnalytics, loadAnalytics]);

  const filteredContent = contentList.filter(item => {
    const matchesStatus = filters.status === 'all' || item.status === filters.status;
    const matchesType = filters.contentType === 'all' || 
      item.content_type?.toLowerCase().includes(filters.contentType.toLowerCase());
    const matchesSearch = !filters.search || 
      item.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
      item.content?.toLowerCase().includes(filters.search.toLowerCase());
    
    return matchesStatus && matchesType && matchesSearch;
  });

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredContent.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredContent.map(item => item.PK)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.size === 0) return;
    
    const confirmMessage = `Are you sure you want to ${action} ${selectedItems.size} item(s)?`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Mock bulk operation - in real implementation, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccess(`Successfully ${action}ed ${selectedItems.size} item(s)`);
      setSelectedItems(new Set());
      setShowBulkActions(false);
      
      // Reload content
      await loadContent();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to ${action} items: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId, itemTitle) => {
    const confirmMessage = `Are you sure you want to delete "${itemTitle}"? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setError(null);
      
      // Mock delete operation - in real implementation, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSuccess(`Successfully deleted "${itemTitle}"`);
      
      // Remove from local state
      setContentList(prev => prev.filter(item => item.PK !== itemId));
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to delete item: ${err.message}`);
    }
  };

  const handleArchiveItem = async (itemId, itemTitle) => {
    try {
      setError(null);
      
      // Mock archive operation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSuccess(`Successfully archived "${itemTitle}"`);
      
      // Update local state
      setContentList(prev => prev.map(item => 
        item.PK === itemId ? { ...item, status: 'archived' } : item
      ));
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to archive item: ${err.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'published': return 'status-published';
      case 'draft': return 'status-draft';
      case 'archived': return 'status-archived';
      default: return 'status-unknown';
    }
  };

  return (
    <ErrorBoundary>
      <div className="content-manager">
        <header className="manager-header">
          <div className="header-left">
            <h1>Content Management</h1>
            <p>Manage articles, news, and homepage content</p>
          </div>
          
          <div className="header-actions">
            <button 
              onClick={() => navigate('/admin/content/new')}
              className="btn-primary"
            >
              + Create New Content
            </button>
          </div>
        </header>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-message">{error}</span>
            <button className="alert-close" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span>
            <span className="alert-message">{success}</span>
            <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        {/* Analytics Dashboard */}
        <section className="analytics-section">
          <h2>Content Analytics</h2>
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="card-value">{analytics.totalContent}</div>
              <div className="card-label">Total Content</div>
            </div>
            <div className="analytics-card">
              <div className="card-value">{analytics.publishedContent}</div>
              <div className="card-label">Published</div>
            </div>
            <div className="analytics-card">
              <div className="card-value">{analytics.draftContent}</div>
              <div className="card-label">Drafts</div>
            </div>
            <div className="analytics-card">
              <div className="card-value">{analytics.archivedContent}</div>
              <div className="card-label">Archived</div>
            </div>
            <div className="analytics-card">
              <div className="card-value">{analytics.recentViews}</div>
              <div className="card-label">Recent Views</div>
            </div>
          </div>
          
          {analytics.popularContent.length > 0 && (
            <div className="popular-content">
              <h3>Popular Content</h3>
              <div className="popular-list">
                {analytics.popularContent.map((item, index) => (
                  <div key={index} className="popular-item">
                    <span className="popular-title">{item.title}</span>
                    <span className="popular-views">{item.views} views</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Filters and Search */}
        <section className="filters-section">
          <div className="filters-row">
            <div className="search-group">
              <input
                type="text"
                placeholder="Search content..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="filter-group">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            
            <div className="filter-group">
              <select
                value={filters.contentType}
                onChange={(e) => handleFilterChange('contentType', e.target.value)}
                className="filter-select"
              >
                <option value="all">All Types</option>
                <option value="news">News</option>
                <option value="about">About</option>
                <option value="club_info">Club Info</option>
              </select>
            </div>
          </div>
          
          {selectedItems.size > 0 && (
            <div className="bulk-actions">
              <span className="selection-count">
                {selectedItems.size} item(s) selected
              </span>
              <div className="bulk-buttons">
                <button 
                  onClick={() => handleBulkAction('publish')}
                  className="btn-bulk btn-publish"
                  disabled={loading}
                >
                  Publish
                </button>
                <button 
                  onClick={() => handleBulkAction('archive')}
                  className="btn-bulk btn-archive"
                  disabled={loading}
                >
                  Archive
                </button>
                <button 
                  onClick={() => handleBulkAction('delete')}
                  className="btn-bulk btn-delete"
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Content List */}
        <section className="content-list-section">
          {loading ? (
            <LoadingSpinner message="Loading content..." />
          ) : filteredContent.length === 0 ? (
            <div className="empty-state">
              <h3>No content found</h3>
              <p>
                {filters.search || filters.status !== 'all' || filters.contentType !== 'all'
                  ? 'Try adjusting your filters or search terms.'
                  : 'Create your first content item to get started.'
                }
              </p>
              <button 
                onClick={() => navigate('/admin/content/new')}
                className="btn-primary"
              >
                Create New Content
              </button>
            </div>
          ) : (
            <div className="content-table">
              <div className="table-header">
                <div className="header-cell checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === filteredContent.length && filteredContent.length > 0}
                    onChange={handleSelectAll}
                  />
                </div>
                <div className="header-cell title-cell">Title</div>
                <div className="header-cell type-cell">Type</div>
                <div className="header-cell status-cell">Status</div>
                <div className="header-cell date-cell">Updated</div>
                <div className="header-cell actions-cell">Actions</div>
              </div>
              
              {filteredContent.map((item) => (
                <div key={item.PK} className="table-row">
                  <div className="table-cell checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.PK)}
                      onChange={() => handleSelectItem(item.PK)}
                    />
                  </div>
                  
                  <div className="table-cell title-cell">
                    <div className="content-title">
                      {item.title || 'Untitled'}
                    </div>
                    <div className="content-preview">
                      {item.content ? (
                        <>
                          {item.content.substring(0, 100)}
                          {item.content.length > 100 ? '...' : ''}
                        </>
                      ) : (
                        <em>No content</em>
                      )}
                    </div>
                  </div>
                  
                  <div className="table-cell type-cell">
                    <span className="content-type-badge">
                      {item.content_type?.replace('CONTENT#', '') || 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="table-cell status-cell">
                    <span className={`status-badge ${getStatusBadgeClass(item.status)}`}>
                      {item.status || 'draft'}
                    </span>
                  </div>
                  
                  <div className="table-cell date-cell">
                    <div className="date-info">
                      <div className="date-primary">
                        {formatDate(item.updated_date)}
                      </div>
                      {item.updated_by && (
                        <div className="date-secondary">
                          by {item.updated_by}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="table-cell actions-cell">
                    <div className="action-buttons">
                      <button
                        onClick={() => navigate(`/admin/content/edit/${item.PK}`)}
                        className="btn-action btn-edit"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      
                      {item.status !== 'archived' && (
                        <button
                          onClick={() => handleArchiveItem(item.PK, item.title)}
                          className="btn-action btn-archive"
                          title="Archive"
                        >
                          📦
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDeleteItem(item.PK, item.title)}
                        className="btn-action btn-delete"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </ErrorBoundary>
  );
}

export default ContentManager;